/**
 * Reports > Fleet Hourly (/reports/fleet-hourly).
 *
 * Every store's trading by hour on one screen, against the same weekday last year:
 * summary tiles (fleet sales against last year over the same hours, traffic,
 * conversion, average sale, how far the data runs) and a grid with one row per store and
 * one column per local hour, a metric switch (Sales, Transactions, Traffic, Conversion),
 * cells shaded against last year, a fleet row, and a day total and percent against last
 * year on each row. Replaces the QuickSight dashboard Store Hourly Dashboard. The
 * store-level version of the same numbers is the Hourly tab on The Sweet Spot's
 * Performance page.
 *
 * This page is deliberately thin. The screen itself is the shared `FleetHourly`
 * component in `@ampersandbrands/ui` (presentational, so The Sweet Spot can reuse it);
 * this file fetches through `useFleetHourly` (refetched every five minutes while the page
 * is open), keeps the chosen day and the open store in the URL so a view is a shareable
 * link, and adds the page chrome: header with the pin toggle, the Excel download, and the
 * store drill-in drawer.
 *
 * RBAC: `reports.fleethourly` sees the whole fleet. A store manager (holding
 * `stores.sweetspot`) reaches an own-store view, enforced server-side like On Order, so
 * the grid then holds that one store. The drill-in reads `GET /v1/stores/omni-hourly`,
 * which accepts `reports.fleethourly` as well as `stores.sweetspot`.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FleetHourly as FleetHourlyScreen } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import {
  FLEET_HOURLY_EARLIEST_DATE,
  downloadFleetHourlyXlsx,
  parseFleetHourlyDate,
  useFleetHourly,
  writeFleetHourlyDate,
} from '@/lib/useFleetHourly';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';
import { FleetHourlyStoreDrawer } from './FleetHourlyStoreDrawer';

/** The accessSlug that grants this report (fleet view). */
const SLUG = 'reports.fleethourly';

/**
 * Fleet through `reports.fleethourly`, or an own-store view through `stores.sweetspot`
 * (the same dual audience as On Order). The api enforces the real scope; this only
 * decides whether to show the page.
 */
function canViewFleetHourly(perms: string[] | undefined, superAdmin = false): boolean {
  if (hasAccessSlug(perms, SLUG, superAdmin)) return true;
  return !!perms?.includes('stores.sweetspot');
}

/** Tomorrow in Denver, the latest day any store can already be on, as YYYY-MM-DD. */
function latestDate(): string {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function FleetHourlyInner() {
  const { user } = useSession();
  const canView = canViewFleetHourly(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const date = useMemo(() => parseFleetHourlyDate(searchParams), [searchParams]);
  const openStore = searchParams.get('store');
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const setDate = useCallback(
    (next: string | null) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeFleetHourlyDate(sp, next);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const setStore = useCallback(
    (storeNo: string | null) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (storeNo === null) sp.delete('store');
          else sp.set('store', storeNo);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const dataQ = useFleetHourly(date, canView);

  const pageState = useMemo(
    () => [
      { label: 'Day', value: date ?? "Each store's own today" },
      ...(openStore ? [{ label: 'Store open', value: openStore }] : []),
    ],
    [date, openStore],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadFleetHourlyXlsx(date);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Fleet Hourly" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Fleet Hourly"
      subtitle="Every store hour by hour against the same weekday last year: sales, transactions, traffic, and conversion. Hours are each store's own clock. Sales refresh every 15 minutes and this page checks again every 5 minutes while it is open."
      actions={<ToolPinToggle toolSlug="fleet-hourly" toolName="Fleet Hourly" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Fleet Hourly" />
      </div>
    );
  }

  const report = dataQ.report;
  const drillStore = openStore ? (report?.stores.find((s) => s.store_no === openStore) ?? null) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <FleetHourlyScreen
        report={report}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        date={date}
        onDateChange={setDate}
        minDate={FLEET_HOURLY_EARLIEST_DATE}
        maxDate={latestDate()}
        onStoreSelect={setStore}
        onExport={() => void handleExport()}
        exporting={exporting}
      />
      {openStore && (
        <FleetHourlyStoreDrawer
          storeNo={openStore}
          storeName={drillStore?.store_name ?? ''}
          fleetStore={drillStore}
          fleetUpdatedAt={report?.updated_at ?? null}
          isToday={date === null}
          onClose={() => setStore(null)}
        />
      )}
    </div>
  );
}

export function FleetHourly() {
  return (
    <ErrorBoundary>
      <FleetHourlyInner />
    </ErrorBoundary>
  );
}
